import { MoonOutlined, SunOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ThemeMode } from './theme';

/** Where the choice is kept, per browser; index.html reads it too, before the first paint. */
export const THEME_STORAGE_KEY = 'wm.theme';

const darkQuery = '(prefers-color-scheme: dark)';

function storedMode(): ThemeMode | null {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function systemMode(): ThemeMode {
  return window.matchMedia?.(darkQuery).matches ? 'dark' : 'light';
}

interface ThemeModeState {
  mode: ThemeMode;
  toggle: () => void;
}

const ThemeModeContext = createContext<ThemeModeState>({ mode: 'light', toggle: () => {} });

/**
 * The theme of the portal: the one the user chose in this browser, otherwise the system's (and
 * it follows the system when that changes). Applied as data-theme on <html>, which switches the
 * CSS custom properties of the theme style sheet (theme.ts); Ant Design follows through
 * appTheme(mode).
 */
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [chosen, setChosen] = useState<ThemeMode | null>(storedMode);
  const [system, setSystem] = useState<ThemeMode>(systemMode);

  useEffect(() => {
    const media = window.matchMedia?.(darkQuery);
    const onSystem = () => setSystem(media?.matches ? 'dark' : 'light');
    // a choice made in another tab of the portal
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) setChosen(storedMode());
    };
    media?.addEventListener('change', onSystem);
    window.addEventListener('storage', onStorage);
    return () => {
      media?.removeEventListener('change', onSystem);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const mode = chosen ?? system;
  // before the paint that shows the new Ant Design styles: the controls' colour transitions would
  // show both themes mixed for a moment, so they are off until the next frames
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (root.dataset.theme === mode) return;
    root.classList.add('wm-theme-switching');
    root.dataset.theme = mode;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.remove('wm-theme-switching')),
    );
  }, [mode]);

  const toggle = useCallback(() => {
    const next: ThemeMode = mode === 'dark' ? 'light' : 'dark';
    setChosen(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // storage refused (private browsing): the choice lasts as long as the page
    }
  }, [mode]);

  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);
  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode(): ThemeModeState {
  return useContext(ThemeModeContext);
}

/** The header's switch: a moon to go dark, a sun to go back to light. */
export function ThemeToggle() {
  const { t } = useTranslation();
  const { mode, toggle } = useThemeMode();
  const label = mode === 'dark' ? t('shell.theme.toLight') : t('shell.theme.toDark');
  return (
    <Tooltip title={label}>
      <Button
        type="text"
        icon={mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
        onClick={toggle}
        aria-label={label}
      />
    </Tooltip>
  );
}
